import { Flex, Text } from '@chakra-ui/react';
import {
  KeyTermUnit,
  MarketDataPercentages,
} from '@client/graphql/__generated__/types';
import isNil from 'lodash/isNil';
import { formatPercent } from '~/common/utils/formatter';
import { formatToCurrency } from '~/services/document/utils/number';
import { BidAskTag } from './BidAskTag';
import { BidAskDetails } from './KeyTermsRow';

export const formatRangeValue = (
  value: number,
  unit: KeyTermUnit,
  isMobile = false
) => {
  if (unit === KeyTermUnit.USD) {
    return formatRangeCurrencyValue(value);
  } else if (unit === KeyTermUnit.DAYS) {
    return `${value} ${isMobile ? 'd' : 'days'}`;
  } else {
    return value;
  }
};

export const formatRangeCurrencyValue = (value?: number) => {
  if (isNil(value)) return;

  const notation = value > 10000 ? 'compact' : 'standard';

  // Properly format millions values with 2 decimal places
  // 1,950,000 should be 1.95M but 19,500,000 should be 19.5M
  const millionsValue = value / 1000000;
  const decimals =
    value >= 1000000
      ? millionsValue < 10
        ? 2
        : millionsValue < 100
          ? 1
          : 0
      : 0;

  return formatToCurrency(value, decimals, notation);
};

export const formatDistance = (distanceMiles: number) => {
  return distanceMiles > 30
    ? '> 30 miles'
    : `${distanceMiles.toFixed(1)} miles`;
};

export function getSaleListColor(
  isPositive: boolean,
  isNegative: boolean
): string;
export function getSaleListColor(saleList: number): string;
export function getSaleListColor(
  p: boolean | number,
  isNegative?: boolean
): string {
  const isPositive = typeof p === 'number' ? p > 0 : p;
  const isNegativeOverride = typeof p === 'number' ? p < 0 : isNegative;
  if (isPositive) {
    return 'green.400';
  } else if (isNegativeOverride) {
    return 'red.400';
  }

  return 'gray.400';
}

export const getDotColor = ({
  unit,
  value,
  range,
}: {
  value?: number | null;
  range?: number[];
  unit?: KeyTermUnit;
}) => {
  if (isNil(value) || isNil(range) || range.length < 2) return 'gray.500';

  if (unit === KeyTermUnit.USD) {
    return value < range[0] ? 'red.500' : 'green.500';
  } else {
    return value <= range[1] ? 'green.500' : 'red.500';
  }
};

export const getMarketDataDisplay = (
  marketData?: MarketDataPercentages,
  suffixOverride?: string
) => {
  if (isNil(marketData)) {
    return null;
  }

  const value = formatPercent(marketData.yes);
  const suffix = suffixOverride || `have contingency`;

  return (
    <Flex alignItems="baseline" gap={1}>
      <Text
        fontSize={{ base: '20px', md: '24px' }}
        fontWeight="medium"
        lineHeight="1"
      >
        {value}
      </Text>
      <Text
        fontSize={{ base: '16px', md: '18px' }}
        fontWeight="medium"
        lineHeight="1"
      >
        {suffix}
      </Text>
    </Flex>
  );
};

export const getKeyTermRangeLabel = ({
  range,
  unit,
  isSingleValue,
}: {
  range?: number[];
  unit?: KeyTermUnit;
  isSingleValue?: boolean;
}) => {
  if (range && unit) {
    return isSingleValue
      ? formatRangeValue(range[0], unit)
      : `${formatRangeValue(range[0], unit)} - ${formatRangeValue(
          range[1],
          unit
        )}`;
  }

  return undefined;
};

export const getPurchasePriceTermLabel = ({
  placeholder,
  listPrice,
  bidAsk,
}: {
  placeholder?: number;
  listPrice?: number;
  bidAsk?: BidAskDetails;
}) => {
  return (
    <Flex
      alignItems={{ base: 'start', md: 'center' }}
      flexDirection={{ base: 'column', md: 'row' }}
      gap={2}
    >
      <Text
        fontSize={{ base: '20px', md: '24px' }}
        fontWeight="medium"
        lineHeight="1"
      >
        {bidAsk?.min && bidAsk?.max && listPrice
          ? getKeyTermRangeLabel({
              range: [listPrice * bidAsk.min, listPrice * bidAsk.max],
              unit: KeyTermUnit.USD,
              isSingleValue: false,
            })
          : formatToCurrency(placeholder)}
      </Text>
      {bidAsk && (
        <BidAskTag
          bidAsk={bidAsk}
          listPrice={listPrice}
          placeholder={placeholder}
        />
      )}
    </Flex>
  );
};

export const roundAdjustedListPrice = (price?: number) => {
  if (!price) return undefined;

  // Round to nearest thousand if price >= 1M
  if (price >= 1000000) {
    return Math.round(price / 1000) * 1000;
  }

  // Round to nearest hundred if price < 1M
  return Math.round(price / 100) * 100;
};

export const formatAgentRecommendation = ({
  value,
  unit,
  shortForm = true,
}: {
  value?: number | null;
  unit?: KeyTermUnit;
  shortForm?: boolean;
}) => {
  if (isNil(value)) return undefined;

  return unit === KeyTermUnit.DAYS
    ? `${value}${shortForm ? 'd' : ' days'}`
    : formatToCurrency(value ?? 0);
};
